// D288: the ONE rule that decides whether a game rates — the user's, verbatim in spirit:
// "the game is ranked if it starts with at least two humans present; bots may fill up empty
// seats", and "the only reason to unrank a game is if it's all bots".
//
// A HUMAN is any seat that is not one of our bots: signed-in or guest alike. Map, speed, VP
// target, discard limit, a scenario or a pasted map no longer un-rank anything (they did
// through H20 — T-RANK-021/022, Q14). It lives in shared because three places must give the
// same answer — the lobby's live checkmark, the play panel's card, and the server at START —
// and a second copy of a rule is a copy that can disagree (D198).

/** How many distinct humans a table needs, at start, to rate. */
export const RANKED_MIN_HUMANS = 2;

export interface RankVerdict {
  ranked: boolean;
  /** The distinct humans counted (one person in two tabs is one human). */
  humans: number;
  /** Plain words for the ⓘ tooltip — why this table does or does not rate. */
  reason: string;
}

export function rankVerdict(humans: number): RankVerdict {
  const ranked = humans >= RANKED_MIN_HUMANS;
  return { ranked, humans, reason: rankReason(ranked, humans) };
}

function rankReason(ranked: boolean, humans: number): string {
  if (ranked) return `Ranked: ${humans} human players are at the table. Bots may fill the empty seats; only the humans' results move ratings.`;
  if (humans <= 0) return 'Unranked: nobody but bots — a game needs at least two human players to count.';
  return 'Unranked: a game needs at least two human players to count. Bots can fill the other seats.';
}

/** One seated player as the ranked count sees it (built server-side, where the address is known). */
export interface RankSeat {
  /** A stable identity key — account id, else guest browser id, else per-connection — or null for a bot. */
  readonly identity: string | null;
  /** A signed-in account: an identity that took a verified email to make. */
  readonly account: boolean;
  /** The address the seat connected from. */
  readonly origin: string;
}

/**
 * True for an address that tells one person from another NOTHING: loopback (every session on
 * a development machine, and every e2e browser, shares it) and the server's "unknown" stand-in.
 * In production the address is the proxy-appended client IP (clientIpFromXff), never loopback,
 * so this only keeps local play and the e2e battery counting the way they always have.
 */
export function originIdentifiesNoOne(origin: string): boolean {
  const o = origin.trim().toLowerCase();
  return (
    o === '' ||
    o === '0.0.0.0' ||
    o === 'localhost' ||
    o === '::1' ||
    o.startsWith('127.') ||
    o.startsWith('::ffff:127.') ||
    /^0{1,4}(:0{1,4}){6}:0{0,3}1$/.test(o) || // ::1 written out in full, as uWS reports it
    /^(0{1,4}:){5}ffff:7f[0-9a-f]{2}:[0-9a-f]{1,4}$/.test(o) // ::ffff:127.x.y.z in full hex form
  );
}

/**
 * Count the distinct humans a table holds, for the ranked verdict.
 *
 * The dedupe has always mattered: the same account in two tabs is ONE person, and H6-S4
 * measured what rating a player against themselves does (a free +162). D299 closes the other
 * half of that hole. A guest's identity is a browser id the CLIENT chooses, so one person with
 * a second socket could seat themselves as a second "human", start a rated table and beat it —
 * a signed-in player farmed the ladder that way, and two guest sockets fed the guest-rank path.
 * So: every distinct ACCOUNT counts (accounts sharing an address are a household, and each cost
 * a verified email); a GUEST counts only when it brings an address no counted human already
 * connected from. Loopback and unknown addresses identify no one and never collapse anybody.
 */
export function countRankedHumans(seats: readonly RankSeat[]): number {
  const counted = new Set<string>();
  const origins = new Set<string>();
  const claim = (origin: string): void => {
    if (!originIdentifiesNoOne(origin)) origins.add(origin);
  };
  for (const s of seats) {
    if (s.identity === null || !s.account || counted.has(s.identity)) continue;
    counted.add(s.identity);
    claim(s.origin);
  }
  for (const s of seats) {
    if (s.identity === null || s.account || counted.has(s.identity)) continue;
    if (!originIdentifiesNoOne(s.origin) && origins.has(s.origin)) continue; // a second socket, not a second person
    counted.add(s.identity);
    claim(s.origin);
  }
  return counted.size;
}
